import { AnyEventObject, createMachine, assign } from 'xstate';
import { doesExist } from 'utils/tsHelpers';
import { pipe } from 'fp-ts/function';
import { FetchResult } from '@apollo/client';
import { rejectGraphqlError } from 'modules/ai/utils';
import { PreviewUsersResponse, PredictiveGoalQuery } from '../../../types';

export const GoalPreviewStates = {
  inactive: 'inactive',
  queryGoalPreview: 'queryGoalPreview',
  queryGoalPreviewExtended: 'queryGoalPreviewExtended',
  ready: 'ready',
  error: 'error',
};

export interface GoalPreviewMachineContext {
  isExtendedLoading: boolean;
  totalCount: number;
}

export const GoalPreviewMachinePlan = {
  predictableActionArguments: true,
  id: 'goalPreviewStates',
  initial: GoalPreviewStates.inactive,
  context: {
    isExtendedLoading: false,
    totalCount: 0,
  },
  states: {
    [GoalPreviewStates.inactive]: {
      on: {
        [GoalPreviewStates.queryGoalPreview]: {
          target: GoalPreviewStates.queryGoalPreview,
        },
      },
    },
    [GoalPreviewStates.queryGoalPreview]: {
      after: {
        6000: {
          target: GoalPreviewStates.queryGoalPreview,
          internal: true,
          actions: 'flagExtendedLoading',
        },
      },
      invoke: {
        src: 'queryPreviewUsers',
        onDone: {
          target: GoalPreviewStates.ready,
          actions: 'handlePreviewUsers',
        },
        onError: {
          target: GoalPreviewStates.error,
        },
      },
    },
    [GoalPreviewStates.queryGoalPreviewExtended]: {
      on: {
        [GoalPreviewStates.ready]: {
          target: GoalPreviewStates.ready,
        },
        [GoalPreviewStates.error]: {
          target: GoalPreviewStates.error,
        },
      },
    },
    [GoalPreviewStates.error]: {
      on: {
        [GoalPreviewStates.queryGoalPreview]: {
          target: GoalPreviewStates.queryGoalPreview,
        },
      },
    },
    [GoalPreviewStates.ready]: {
      on: {
        [GoalPreviewStates.queryGoalPreview]: {
          target: GoalPreviewStates.queryGoalPreview,
        },
      },
    },
  },
};

export const GoalPreviewMachine = createMachine<GoalPreviewMachineContext>(GoalPreviewMachinePlan);

export interface PreviewUsersCountEvent extends AnyEventObject {
  goal: PredictiveGoalQuery;
}
export interface ReceiveUsersPreviewCount extends AnyEventObject {
  data: {
    data: {
      estimatePredictiveGoalUserCount: PreviewUsersResponse;
    };
  };
}

export type GoalPreviewMachineEffects = {
  /**
   @description: Request to query goal preview data
   */
  queryPreviewUsers: (vars: {
    goal: PredictiveGoalQuery;
  }) => Promise<FetchResult<PreviewUsersResponse>>;
};

export const createGoalPreviewMachineEffects = <TContext extends GoalPreviewMachineContext>({
  queryPreviewUsers,
}: GoalPreviewMachineEffects) => ({
  actions: {
    flagExtendedLoading: assign((context: TContext) => {
      return {
        ...context,
        isExtendedLoading: true,
      };
    }),
    handlePreviewUsers: assign((context: TContext, event: unknown) => {
      const estimatedUserCount = (event as ReceiveUsersPreviewCount)?.data?.data
        ?.estimatePredictiveGoalUserCount?.total_count;
      return {
        ...context,
        totalCount: doesExist(estimatedUserCount) ? estimatedUserCount : context.totalCount,
        isExtendedLoading: false,
      };
    }),
  },
  services: {
    queryPreviewUsers: (_: unknown, event: unknown) => {
      return pipe(
        { goal: (event as PreviewUsersCountEvent).goal },
        queryPreviewUsers,
        rejectGraphqlError,
      );
    },
  },
});
